import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Modal, IModalProps, message } from '@byte-design/ui';
import styles from './index.module.scss';
import { AdsLogin, AdsLoginProps } from '../../../AdsLogin';
import { AdsRegister, AdsRegisterProps } from '../../../AdsRegister';

const ADS_APP_ID = 4684;

function LoginModal(
  props: IModalProps & { loginModalType?: 'register' | 'login'; cb?: Function; store: any }
) {
  const { cb, store, show, loginModalType } = props;
  const isBoe = window.location.href.indexOf('boe') !== -1;
  const [type, setType] = useState<'register' | 'login'>(loginModalType!);

  const loginSuccess = useCallback(
    async (data: any) => {
      // add loading style on the login button
      const loginBtnEl = document.querySelector('#TikTok_Ads_SSO_Login_Btn');
      const loadingIconEl = document.querySelector('#TikTok_Ads_SSO_Login_Btn > i');
      loginBtnEl?.classList.add('is-disabled');
      loadingIconEl?.setAttribute('style', 'display: inline-block;');
      // create iframe to open sso page
      const iframe = document.createElement('iframe');

      const redirectUrl = new URL(data.redirect_url);
      redirectUrl.protocol = 'https:';
      iframe.src = redirectUrl.href;
      iframe.style.display = 'none';
      iframe.onload = async () => {
        // if callbackUrl is given, go to the callback url, otherwise fetch user info
        try {
          if (cb) {
            await cb();
            await store.fetchUserInfo();
          } else {
            const callbackUrl = new URL(window.location.href).searchParams.get('callbackUrl') as string;
            const url = new URL(callbackUrl);
            window.location.href = url.href;
          }
        } catch {
          await store.fetchUserInfo();
          if (!store.loggedIn) {
            message.error('Failed to log in, please refresh the page and try again later.');
          }
          document.body.removeChild(iframe);
        }
        // login finished
        // after iframe loaded, cookie has been set, remove loading style on the login button
        loginBtnEl?.classList.remove('is-disabled');
        loadingIconEl?.setAttribute('style', 'display: none;');
      };
      document.body.appendChild(iframe);
    },
    [cb]
  );

  const commonParams: AdsLoginProps['ssoConfig'] & AdsRegisterProps['ssoConfig'] = useMemo(
    () => ({
      id: 'login',
      width: 352,
      className: styles.loginBody,
      lang: store.lng,
      appId: ADS_APP_ID,
      accountTypes: ['email', 'mobile'], // 账户类型，支持手机/邮箱，优先展示第一个
      service: new URL('/business', window.location.origin).href, // redirect url after the user is logged in
      defaultCountry: 'US'
    }),
    [store.lng]
  );

  const loginConfig = useMemo(
    () => ({
      ...commonParams,
      host: isBoe
        ? `https://${atob('YnVzaW5lc3Mtc3NvLWJvZS5ieXRlZGFuY2UubmV0')}/`
        : 'https://business-sso.tiktok.com/',
      openSSO: true,
      signUp: () => {
        setType('register');
      },
      loginSuccess
    }),
    [isBoe, commonParams, loginSuccess]
  );

  const registerConfig = useMemo(
    () => ({
      ...commonParams,
      ssoConfigs: {
        domain: isBoe ? atob('YnVzaW5lc3Mtc3NvLWJvZS5ieXRlZGFuY2UubmV0') : 'business-sso.tiktok.com',
        openSSO: true
      },
      signIn: () => {
        setType('login');
      },
      registerSuccess: () => {
        setType('login');
      }
    }),
    [isBoe, commonParams]
  );

  useEffect(() => {
    if (show && loginModalType) {
      setType(loginModalType!);
    }
  }, [show, type]);

  useEffect(() => {
    if (loginModalType) {
      setType(loginModalType);
    }
  }, [loginModalType]);

  return (
    <Modal width={416} wrapperClassName={styles.modalContainer} {...props}>
      {type === 'login' && <AdsLogin className={styles.loginContainer} ssoConfig={loginConfig} />}
      {type === 'register' && <AdsRegister className={styles.loginContainer} ssoConfig={registerConfig} />}
    </Modal>
  );
}

export default observer(LoginModal);
